import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode, type Ref } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from './lib';
import { emailProblem } from './email-rules';
import './Field.css';

type TextFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  suffix?: string;
  /* `datetime-local` joined the list for the admin round's notice window:
     a notice that starts on Monday at eight needs both halves, and the pair
     of converters that keeps it honest across a zone lives beside the
     screens that use it. */
  type?: 'text' | 'number' | 'tel' | 'email' | 'password' | 'date' | 'time' | 'datetime-local';
  error?: string;
  required?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  name?: string;
  disabled?: boolean;
  /* A bound the browser enforces as it types — a code, a short label. */
  maxLength?: number;
  /* Bounds for date, time and number inputs, enforced by the picker
     itself: an end date that cannot be set before its start beats a
     validation message after the fact. */
  min?: string | number;
  max?: string | number;
  step?: string | number;
  /* One quiet sentence under the control: where the value comes from,
     what it does, who may change it. A field that needs explaining used
     to get a paragraph somewhere else on the page. */
  help?: string;
  /* The phone keyboard a field asks for where its type alone cannot say
     it: a text field that takes an email OR a mobile number (Uganda's
     sign-in, U-01) asks for the email keyboard, which still has digits; a
     six-digit code asks for the number pad; an amount typed as text (so it
     reads with a point in every browser's language) asks for the decimal
     pad. Absent, the input carries no such attribute, exactly as before. */
  inputMode?: 'email' | 'tel' | 'numeric' | 'decimal' | 'text';
  /* Pins the control itself for a spec, exactly as TextAreaField already
     does. It lands on the input, never the wrapping label, so a fill
     addresses the thing a person types into. */
  'data-testid'?: string;
};

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  suffix,
  type = 'text',
  error,
  required,
  autoFocus,
  autoComplete,
  name,
  disabled,
  maxLength,
  min,
  max,
  step,
  help,
  inputMode,
  'data-testid': testId,
}: TextFieldProps) {
  /* Show/hide toggle for password fields: added at the shared field
     so every login, reset, invite-accept and vault-secret input on
     every app gets it automatically. Text stays type=password when
     the eye is off, so autofill and password managers still bind. */
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const effectiveType = isPassword && revealed ? 'text' : type;
  /* An address typed on a phone: the keyboard shows @ and .co.ke without a
     shift, and nothing capitalises the first letter or "corrects" the domain
     into a dictionary word. Set here rather than at each of the forty-odd
     email fields, because every one of them wants the same thing and the ones
     that were missing it were the ones people fill in on a handset. */
  const emailInput = type === 'email' || inputMode === 'email'
    ? { inputMode: 'email' as const, autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }
    : inputMode
      ? { inputMode }
      : {};
  /* The browser's own memory stays out of Meridian's fields unless a field
     asks for it (the hospital list round, P1). In the prescribe panel the
     browser laid "amox", typed there an hour before, over the medicine
     list as a black bubble; on a shared desk it also offers one patient's
     name and phone to the next. A sign-in field names what it is
     (`email`, `current-password`) and keeps the password manager; a
     password field without a name is left to the browser. */
  const effectiveAutoComplete = autoComplete ?? (isPassword ? undefined : 'off');
  /* An email field checks what was typed once the person leaves it, by the
     rules the server keeps (email-rules.ts, the round of 1 November P-10:
     "ssali.joshua.@dhs.africa" went through every form and stopped a
     hospital's provisioning). The caller's own error wins. */
  const [left, setLeft] = useState(false);
  const emailError = type === 'email' && left && value.trim() ? emailProblem(value) : null;
  const shownError = error || emailError || undefined;
  return (
    <label className={cn('mrd-field', shownError && 'mrd-field--error')}>
      <span className="mrd-field__label">
        {label}
        {required && <span className="mrd-field__req"> *</span>}
      </span>
      <span className="mrd-field__control">
        <input
          className={cn('mrd-field__input', isPassword && 'mrd-field__input--password')}
          type={effectiveType}
          value={value}
          name={name}
          placeholder={placeholder}
          maxLength={maxLength}
          min={min}
          max={max}
          /* A number field takes any decimal unless it names its own step
             (the hospital list round, item 162): the browser's default step
             of 1 called a supplier's 125.75 invalid. Whether a currency has
             cents is the server's rule (`shared/money-input.ts`). */
          step={step ?? (type === 'number' ? 'any' : undefined)}
          autoFocus={autoFocus}
          autoComplete={effectiveAutoComplete}
          required={required}
          disabled={disabled}
          data-testid={testId}
          {...emailInput}
          aria-invalid={shownError ? true : undefined}
          onBlur={type === 'email' ? () => setLeft(true) : undefined}
          onChange={(event) => onChange(event.target.value)}
        />
        {isPassword && (
          <button
            type="button"
            className="mrd-field__reveal"
            onClick={() => setRevealed((v) => !v)}
            /* Deliberately avoid the word "password" in the label so
               Playwright's getByLabel('Password') resolves to the
               input alone and not this button as well (the button
               is nested inside the wrapping <label>, otherwise its
               name would inherit the field's label text). */
            aria-label={revealed ? 'Hide entered text' : 'Reveal entered text'}
            aria-pressed={revealed}
            tabIndex={-1}
          >
            {revealed ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
          </button>
        )}
        {suffix && !isPassword && <span className="mrd-field__suffix">{suffix}</span>}
      </span>
      {help && !shownError && <span className="mrd-field__help">{help}</span>}
      {shownError && <span className="mrd-field__message">{shownError}</span>}
    </label>
  );
}

type SelectFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /* Names the control the way TextField already does, so a form posts a
     readable field and a test can address it without quoting its label. */
  name?: string;
  /* One quiet sentence under the control, exactly as TextField's help
     does. A choice whose consequence needs a sentence — what a posting
     option will do to the books — had no place to put it. */
  help?: string;
  'data-testid'?: string;
};

export function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
  disabled,
  name,
  help,
  'data-testid': testId,
}: SelectFieldProps) {
  return (
    <label className="mrd-field">
      <span className="mrd-field__label">
        {label}
        {required && <span className="mrd-field__req"> *</span>}
      </span>
      <span className="mrd-field__control">
        <select
          className="mrd-field__input"
          aria-label={label}
          name={name}
          value={value}
          disabled={disabled}
          data-testid={testId}
          onChange={(event) => onChange(event.target.value)}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </span>
      {help && <span className="mrd-field__help">{help}</span>}
    </label>
  );
}

type TextAreaFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  /* Names the control the way TextField already does, so a form posts a
     readable field and a test can address it without quoting its label. */
  name?: string;
  disabled?: boolean;
  /* A ceiling the browser enforces as it types — the same one the server
     holds, so a long report never overshoots what the draft can keep. */
  maxLength?: number;
  /* The element itself, for a caller that inserts at the caret (the lab
     narrative's heading chips). */
  textareaRef?: Ref<HTMLTextAreaElement>;
  /* Grows with what is typed, up to most of the screen's height, then
     scrolls inside itself (the hospital list round, P3: a long ward note
     ran past the edge). `rows` stays the resting height. */
  autoGrow?: boolean;
  'data-testid'?: string;
  /* A sentence under the box when the value is wrong or missing. */
  error?: string;
  required?: boolean;
};

export function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  name,
  disabled,
  maxLength,
  textareaRef,
  autoGrow,
  error,
  required,
  'data-testid': testId,
}: TextAreaFieldProps) {
  const own = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = own.current;
    if (!autoGrow || !el) return;
    el.style.height = 'auto';
    const cap = Math.round(window.innerHeight * 0.6);
    el.style.height = `${Math.min(el.scrollHeight + 2, cap)}px`;
    el.style.overflowY = el.scrollHeight + 2 > cap ? 'auto' : 'hidden';
  }, [autoGrow, value]);
  const setRef = (el: HTMLTextAreaElement | null) => {
    own.current = el;
    if (typeof textareaRef === 'function') textareaRef(el);
    else if (textareaRef) (textareaRef as { current: HTMLTextAreaElement | null }).current = el;
  };
  return (
    <label className={cn('mrd-field', error && 'mrd-field--error')}>
      <span className="mrd-field__label">
        {label}
        {required && <span className="mrd-field__req"> *</span>}
      </span>
      <textarea
        ref={setRef}
        aria-invalid={error ? true : undefined}
        className={cn('mrd-field__input mrd-field__textarea', autoGrow && 'mrd-field__textarea--grow')}
        value={value}
        rows={rows}
        name={name}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        data-testid={testId}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && <span className="mrd-field__message">{error}</span>}
    </label>
  );
}

/* The column count is a custom property rather than an inline
   grid-template-columns, so the stylesheet can collapse the form to one column
   on a phone. Written inline it could not be overridden, and a three-column
   form on a 390px screen gave each field 103px — narrow enough that a select
   showed part of its own value. */
export function FormGrid({ children, columns = 2 }: { children: ReactNode; columns?: 1 | 2 | 3 }) {
  return (
    <div className="mrd-formgrid" style={{ '--mrd-formgrid-cols': columns } as CSSProperties}>
      {children}
    </div>
  );
}
